/* Copyright (c) 2020-present CloudIO Inc */

import { Alert, AlertColor, Button } from '@mui/material';
import Icon from 'icons/Icon';
import {
  AnyManager,
  DSType,
  Item,
  Page,
  Platform,
} from '../../../../componentregistry';

export function severityToInt(severity?: AlertColor) {
  switch (severity) {
    case 'info':
    case 'success':
      return 3;
    case 'warning':
      return 2;
    default:
      return 1;
  }
}

export interface AnalyzeContext {
  mgr: AnyManager;
  appUid: string;
  pageId: string;
  itemId: string;
  platform: Platform;
  page: Page;
}

export type AnalyzeResult = [AnalyzeIssue[], (() => void)[]] | null;

export type AnalyzeFunction = (props: AnalyzeContext) => Promise<AnalyzeResult>;

export interface AnalyzeIssue {
  key: string;
  itemId: string;
  showItemId?: string;
  fix?: () => void;
  alert: string;
  tip?: string;
  severity?: AlertColor;
}

interface MakeProps extends AnalyzeIssue {
  platform: Platform;
  appUid: string;
  pageId: string;
}

export function makeAlert({
  key,
  appUid,
  pageId,
  itemId,
  platform,
  fix,
  showItemId,
  alert,
  tip,
  severity = 'error',
}: MakeProps) {
  return (
    <Alert
      key={key}
      sx={{ marginTop: 2 }}
      severity={severity}
      action={
        <>
          {fix ? (
            <Button color="inherit" size="small" onClick={fix}>
              Fix It
            </Button>
          ) : null}
          {showItemId ? (
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                platform.selectItemInDesigner({
                  appUid,
                  pageId,
                  itemId: showItemId,
                });
              }}
            >
              Show Item
            </Button>
          ) : null}
        </>
      }
    >
      {alert}
      {tip ? (
        <Icon
          sx={{ cursor: 'help', marginLeft: 2 }}
          color="error"
          iconName="question"
          data-tip-width={600}
          data-tip={tip}
          data-tip-markdown
          data-tip-delay={0}
        />
      ) : null}
    </Alert>
  );
}

export function getDataStoreItem({
  page,
  alias,
}: {
  page: Page;
  alias: string;
}): Item<DSType> | undefined {
  const itemIds = Object.keys(page);
  return itemIds
    .filter(
      (id) =>
        page[id]?.type === 'CloudDataStore' ||
        page[id]?.type === 'DataStore' ||
        page[id]?.type === 'ReadOnlyDataStore' ||
        page[id]?.type === 'LocalDataStore' ||
        page[id]?.type === 'TransientDataStore',
    )
    .map((id) => page[id] as Item<DSType>)
    .find((ds) => ds.props.alias === alias);
}
